import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AllCartCollactionThunk } from "../../redux/states/cartCollection/thunk";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import {
  Eye,
  Search,
  Filter,
  RefreshCw,
  AlertCircle,
  Calendar,
  User,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

const ManageAllCartData = () => {
  const dispatch = useDispatch();
  const { cart, loading, error } = useSelector((state) => state.cart);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;

  // ⭐ Fetch data
  useEffect(() => {
    dispatch(AllCartCollactionThunk());
  }, [dispatch]);

  // ⭐ Filter
  useEffect(() => {
    if (cart?.length > 0) {
      const filtered = cart.filter(
        (item) =>
          item?.cart_user?.name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          item?.product_data?.name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          item?.order_status?.toLowerCase().includes(searchTerm.toLowerCase())
      );

      setFilteredData(filtered);
    } else {
      setFilteredData([]);
    }
  }, [cart, searchTerm]);

  // ⭐ View page
  const handleView = (uuid) => {
    navigate(`/view-cart-collection/${uuid}`);
  };

  // ⭐ Refresh
  const handleRefresh = () => {
    dispatch(AllCartCollactionThunk());
    toast.success("Cart list refreshed!");
  };

  return (
    <NonAuthLayout>
      <div className="min-h-screen p-2">
        {/* Header */}
        <div className="mb-3 flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
              Cart Items Manage
            </h1>
            <p className="text-gray-600 mt-2">List and manage all cart item</p>
          </div>

          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-xl"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {/* Search */}
        <div className="bg-white rounded-2xl border p-6 mb-2">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 text-gray-400" />
              <input
                className="w-full pl-10 pr-4 py-3 border rounded-xl"
                placeholder="Search user / product / status..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <button className="px-4 py-3 bg-gray-100 rounded-xl">
              <Filter />
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border overflow-hidden">
          <div className="overflow-x-scroll">
            <table className="min-w-full divide-y">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">S.No</th>
                  <th className="px-6 py-3 text-left">User</th>
                  <th className="px-6 py-3 text-left">Product</th>
                  <th className="px-6 py-3 text-left">Image</th>
                  <th className="px-6 py-3 text-left">Status</th>
                  <th className="px-6 py-3 text-left">Date</th>
                  <th className="px-6 py-3 text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-10">
                      <RefreshCw className="animate-spin mx-auto" />
                      Loading...
                    </td>
                  </tr>
                ) : filteredData.length > 0 ? (
                  filteredData.map((item, index) => (
                    <tr key={item.uuid} className="hover:bg-gray-50">
                      <td className="px-6 py-4">{index + 1}</td>

                      <td className="px-6 py-4  items-center gap-2">
                        {item?.cart_user?.name || "N/A"}

                        {item?.view_status === "unseen" && (
                          <span className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700">
                            NEW
                          </span>
                        )}
                      </td>

                      <td className="px-6 py-4">
                        {item?.product_data?.name || "N/A"}
                      </td>

                      <td className="px-6 py-4">
                        <img
                          src={`${API_URL}/${item?.product_data?.image}`}
                          className="h-16 rounded-lg border"
                          alt="product"
                        />
                      </td>

                      <td className="px-6 py-4">
                        <span className="px-3 py-1 rounded-full bg-yellow-100 text-yellow-700">
                          {item?.order_status}
                        </span>
                      </td>

                      <td className="px-6 py-4">
                        <Calendar className="inline w-4 mr-2" />
                        {new Date(item?.created_at).toLocaleDateString("en-IN")}
                      </td>

                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => handleView(item.uuid)}
                          className="p-2 text-blue-600"
                        >
                          <Eye />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-10 text-gray-500">
                      <AlertCircle className="mx-auto mb-3" />
                      No cart item found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </NonAuthLayout>
  );
};

export default ManageAllCartData;
